import { useNavigate, useLocation } from 'react-router-dom';
import { useState } from 'react';
import { LayoutDashboard, LogOut, Menu, X, Users, FileCheck, Clock, Shield, FileBarChart, CalendarCheck } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { adminAPI } from '@/lib/api';

const sidebarItems = [
  { path: '/rm', icon: LayoutDashboard, label: 'Dashboard' },
  { path: '/rm/employees', icon: Users, label: 'My Team' },
  { path: '/rm/attendance', icon: CalendarCheck, label: 'Staff Attendance' },
  { path: '/rm/leaves', icon: FileCheck, label: 'Leave Requests' },
  { path: '/rm/sanctions', icon: Clock, label: 'Late Sanctions' },
  { path: '/rm/permits', icon: Shield, label: 'Work Permits' },
  { path: '/rm/reports', icon: FileBarChart, label: 'Reports' },
];

export default function RMLayout({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const user = JSON.parse(localStorage.getItem('user') || '{}');

  const handleLogout = async () => {
    try { await adminAPI.logout(); } catch {}
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/');
  };

  return (
    <div className="min-h-screen min-h-[100dvh] bg-[#f4f4f5]">
      {/* Mobile header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 z-40 bg-[#1a2529] h-14 flex items-center px-4 justify-between">
        <button onClick={() => setOpen(!open)} className="text-white">
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
        <span className="text-white font-bold text-sm tracking-wide">UPASTITH RM</span>
        <div className="w-8" />
      </header>

      {/* Overlay */}
      {open && <div className="lg:hidden fixed inset-0 z-30 bg-black/40" onClick={() => setOpen(false)} />}

      {/* Sidebar */}
      <aside className={cn(
        "fixed top-0 left-0 z-40 h-full w-64 bg-[#1a2529] transform transition-transform duration-200 lg:translate-x-0",
        open ? "translate-x-0" : "-translate-x-full"
      )}>
        <div className="p-5 border-b border-white/10">
          <h2 className="text-white font-extrabold text-lg tracking-tight">UPASTITH</h2>
          <p className="text-[#dea439] text-xs font-semibold mt-0.5">{user.name || 'Reporting Manager'}</p>
        </div>
        <nav className="p-3 space-y-0.5">
          {sidebarItems.map(item => {
            const Icon = item.icon;
            const active = item.path === '/rm'
              ? location.pathname === '/rm'
              : location.pathname.startsWith(item.path);
            return (
              <button
                key={item.path}
                data-testid={`rm-nav-${item.label.toLowerCase().replace(/\s/g, '-')}`}
                onClick={() => { navigate(item.path); setOpen(false); }}
                className={cn(
                  "w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors",
                  active ? "bg-[#026536] text-white" : "text-white/50 hover:text-white hover:bg-white/5"
                )}
              >
                <Icon size={18} />
                {item.label}
              </button>
            );
          })}
        </nav>
        <div className="absolute bottom-0 left-0 right-0 p-3 border-t border-white/10">
          <Button data-testid="rm-logout-btn" variant="ghost" onClick={handleLogout} className="w-full justify-start text-white/40 hover:text-white hover:bg-white/5 gap-2">
            <LogOut size={18} /> Logout
          </Button>
        </div>
      </aside>

      {/* Main content */}
      <main className="lg:ml-64 pt-14 lg:pt-0 min-h-screen">
        <div className="p-4 lg:p-8 max-w-7xl">
          {children}
        </div>
      </main>
    </div>
  );
}
